/**
 * Euler Protocol Contract Addresses
 *
 * Centralized configuration for all Euler-related contract addresses.
 * Derives addresses from @eulerxyz/core-sdk contractBook where possible.
 * Import from this file across eLend, eVault, eBorrow features.
 */

import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { contractBook } from '@eulerxyz/core-sdk'
import type { Address } from 'viem'

// Note: do not import from `./market-config` or `@/features/evault-details/data/products` in
// this file. `consts.ts` re-exports from here, so any transitive import chain that
// reaches `consts.ts` creates a TDZ cycle on APP_ORIGIN. EBorrow-category derivation
// lives in `./market-config` instead.

// =============================================================================
// Chain IDs
// =============================================================================

/** Default chain for Euler Earn Vaults */
export const E_EARN_VAULT_CHAIN_ID = 999

/** Default chain for Euler Lending Vaults */
export const E_LEND_CHAIN_ID = 999

// =============================================================================
// API URLs
// =============================================================================

/** Legacy proxy URL — account positions still route through the old proxy during transition */
export const EULER_INDEXER_PROXY_URL = '/api/euler-indexer'
export const EULER_ORACLE_CHECKS_URL = 'https://oracle-dashboard-production.up.railway.app/api/oracle'
export const EULER_SWAP_API_URL = 'https://euler-orderflow-router-production.up.railway.app'
// export const EULER_SWAP_API_URL = 'http://localhost:3001'

// =============================================================================
// Display Settings
// =============================================================================

/**
 * Display mode for vaults
 * - true: Show all vaults from indexer (no filtering)
 * - false: Show only verified vaults (filtered by perspective contracts)
 */
export const SHOW_ALL_EVAULTS =
  process.env.NEXT_PUBLIC_HIDE_EVAULTS !== '1' &&
  (process.env.NODE_ENV === 'development' ||
    process.env.NEXT_PUBLIC_DEV_MODE === '1' ||
    process.env.NEXT_PUBLIC_VERCEL_ENV === 'preview')
export const SHOW_ALL_ELEND_VAULTS =
  process.env.NEXT_PUBLIC_HIDE_ELEND_VAULTS !== '1' &&
  (process.env.NODE_ENV === 'development' ||
    process.env.NEXT_PUBLIC_DEV_MODE === '1' ||
    process.env.NEXT_PUBLIC_VERCEL_ENV === 'preview')

// =============================================================================
// Oracle Contract Addresses (app-specific, not in core-sdk)
// =============================================================================

/** USD Unit of Account - represents USD with 18 decimals */
export const EULER_USD_UNIT_OF_ACCOUNT = CheckedAddress('0x0000000000000000000000000000000000000348')

/** USD Reference Token (USDT) - used for price conversions */
export const USD_REFERENCE_TOKEN = CheckedAddress('0xb8ce59fc3717ada4c02eadf9682a9e934f625ebb')

// =============================================================================
// Lens Contract Addresses & ABIs (from core-sdk contractBook)
// =============================================================================

type LensChainKey = keyof typeof contractBook.vaultLens.address

/** VaultLens - provides vault info including oracle prices via liabilityPriceInfo */
export const VAULT_LENS_ADDRESS = CheckedAddress(
  contractBook.vaultLens.address[E_LEND_CHAIN_ID as LensChainKey] as string,
)
export const vaultLensAbi = contractBook.vaultLens.abi

/** EulerEarnVaultLens - provides Earn vault info including strategies */
export const EULER_EARN_VAULT_LENS_ADDRESS = CheckedAddress(
  contractBook.eulerEarnVaultLens.address[E_LEND_CHAIN_ID as LensChainKey] as string,
)
export const eulerEarnVaultLensAbi = contractBook.eulerEarnVaultLens.abi

// =============================================================================
// Protocol Contract Addresses (from core-sdk contractBook)
// =============================================================================

/** Euler Vault Connector (EVC) - manages vault interactions */
export const EVC_ADDRESS = CheckedAddress(
  contractBook.evc.address[E_LEND_CHAIN_ID as keyof typeof contractBook.evc.address] as string,
)

/** AccountLens - computes account-wide liquidity */
export const ACCOUNT_LENS_ADDRESS = CheckedAddress(
  contractBook.accountLens.address[E_LEND_CHAIN_ID as LensChainKey] as string,
)

// =============================================================================
// Perspective Contract Addresses (derived from core-sdk contractBook)
// =============================================================================

/**
 * Perspective contracts verify which vaults belong to specific categories.
 * Use `isVerified(vault)` to check if a vault is in a perspective.
 * Use `verifiedArray()` to get all vaults in a perspective.
 */
export interface PerspectiveAddresses {
  /** Escrow collateral perspective - vaults with escrow collateral */
  escrowedCollateral: Address
  /** Governed perspective - vaults with governance */
  governed: Address
  /** EVK Factory perspective - factory-deployed lending vaults */
  evkFactory: Address
  /** Edge Factory perspective - edge case vaults */
  edgeFactory: Address
  /** Euler Earn Factory perspective - factory-deployed earn vaults */
  eulerEarnFactory: Address
  /** Euler Earn Governed perspective - governed earn vaults (Managed) */
  eulerEarnGoverned: Address
  /** Ungoverned 0x perspective */
  ungoverned0x?: Address
  /** Ungoverned Nzx perspective */
  ungovernedNzx?: Address
}

type ChainKey = keyof typeof contractBook.governedPerspective.address

function getPerspectiveAddress(entry: { address: Record<ChainKey, Address[]> }, chainId: number): Address {
  const addrs = entry.address[chainId as ChainKey]
  return addrs?.[0] ?? ('0x0000000000000000000000000000000000000000' as Address)
}

function buildPerspectiveAddresses(chainId: number): PerspectiveAddresses {
  return {
    escrowedCollateral: getPerspectiveAddress(contractBook.escrowPerspective, chainId),
    governed: getPerspectiveAddress(contractBook.governedPerspective, chainId),
    evkFactory: getPerspectiveAddress(contractBook.factoryPerspective, chainId),
    edgeFactory: getPerspectiveAddress(contractBook.edgePerspective, chainId),
    eulerEarnFactory: getPerspectiveAddress(contractBook.eulerEarnFactoryPerspective, chainId),
    eulerEarnGoverned: getPerspectiveAddress(contractBook.eulerEarnGovernedPerspective, chainId),
    ungoverned0x: getPerspectiveAddress(contractBook.ungoverned0xPerspective, chainId),
    ungovernedNzx: getPerspectiveAddress(contractBook.ungovernedNzxPerspective, chainId),
  }
}

/**
 * Perspective addresses by chain ID (derived from contractBook)
 */
export const PERSPECTIVE_ADDRESSES: Record<number, PerspectiveAddresses> = {
  [E_LEND_CHAIN_ID]: buildPerspectiveAddresses(E_LEND_CHAIN_ID),
}

/**
 * Get perspective addresses for a chain
 * @param chainId - The chain ID
 * @returns Perspective addresses or undefined if chain not supported
 */
export function getPerspectiveAddresses(chainId: number): PerspectiveAddresses | undefined {
  if (PERSPECTIVE_ADDRESSES[chainId]) return PERSPECTIVE_ADDRESSES[chainId]
  // Dynamically build from contractBook for other supported chains
  const governed = contractBook.governedPerspective.address[chainId as ChainKey]
  if (!governed) return undefined
  return buildPerspectiveAddresses(chainId)
}

// =============================================================================
// Pre-wrapped Perspective Addresses for Default Chains (CheckedAddress)
// =============================================================================

/** Governed perspective address for eLend/eBorrow (HyperEVM) */
export const E_LEND_GOVERNED_PERSPECTIVE = CheckedAddress(
  PERSPECTIVE_ADDRESSES[E_LEND_CHAIN_ID]?.governed ?? '0x0000000000000000000000000000000000000000',
)

/** Euler Earn Governed perspective address for eVault (HyperEVM) */
export const E_EARN_GOVERNED_PERSPECTIVE = CheckedAddress(
  PERSPECTIVE_ADDRESSES[E_EARN_VAULT_CHAIN_ID]?.eulerEarnGoverned ?? '0x0000000000000000000000000000000000000000',
)

// =============================================================================
// Default Perspective Settings
// =============================================================================

/**
 * Default enabled perspectives for eLend vaults
 * These are shown by default in the UI
 * Note: escrowedCollateral excluded - escrow vaults are internal to Earn vaults
 * and should not be shown to users for direct deposit
 */
export function getDefaultEnabledPerspectives(chainId: number): Address[] {
  const addresses = getPerspectiveAddresses(chainId)
  if (!addresses) return []

  return [addresses.governed].filter((addr) => addr !== '0x0000000000000000000000000000000000000000')
}

/**
 * Default disabled perspectives for eLend vaults
 * These are hidden by default in the UI (unverified vaults)
 * Note: edgeFactory is enabled by default (not disabled) - matches elendsQuery.ts
 */
export function getDefaultDisabledPerspectives(chainId: number): Address[] {
  const addresses = getPerspectiveAddresses(chainId)
  if (!addresses) return []

  return [addresses.evkFactory, addresses.eulerEarnFactory].filter(
    (addr) => addr !== '0x0000000000000000000000000000000000000000',
  )
}

// =============================================================================
// Vault Status Helpers
// =============================================================================

/**
 * Vault status based on perspective membership
 */
export enum VaultPerspectiveStatus {
  /** Vault is in the earnGoverned perspective - fully managed/governed */
  Managed = 'Managed',
  /** Vault is in earnFactory perspective but not governed - factory deployed */
  Factory = 'Factory',
  /** Vault is in the governed perspective - governed lending vault */
  Governed = 'Governed',
  /** Vault is in escrow perspective */
  Escrow = 'Escrow',
  /** Vault is not in any known perspective */
  Unknown = 'Unknown',
}

/**
 * Check if a chain is supported for Euler
 */
export function isEulerSupportedChain(chainId: number): boolean {
  return !!contractBook.governedPerspective.address[chainId as ChainKey]
}

/**
 * Get all supported Euler chain IDs
 */
export function getEulerSupportedChainIds(): number[] {
  return Object.keys(contractBook.governedPerspective.address).map(Number)
}

// =============================================================================
// Vault Categories for Filtering
// =============================================================================

/** eVault (Euler Earn) categories */
export type EVaultCategory = 'All' | 'Clearstar' | 'Stablecoins' | 'Staking' | 'Delta neutral'
export const E_VAULT_CATEGORIES: EVaultCategory[] = ['All', 'Clearstar', 'Stablecoins', 'Staking', 'Delta neutral']

/**
 * eBorrow filter-chip label. Values are derived from `CLUSTER_CONFIG` at
 * runtime (see `E_BORROW_CATEGORIES` in `./market-config`); widened to `string`
 * because cluster labels aren't known at this module's evaluation time.
 */
export type EBorrowCategory = string

// The concrete `E_BORROW_CATEGORIES` array lives in `./market-config` to avoid
// a `consts.ts` ↔ `euler-contracts.ts` import cycle. Consumers should import
// `E_BORROW_CATEGORIES` from `@/config/market-config`.

/** Common stablecoin symbols for filtering */
export const STABLECOIN_SYMBOLS = [
  'USDC',
  'USDT',
  'DAI',
  'USDC.e',
  'USDbC',
  'FRAX',
  'LUSD',
  'GHO',
  'USDH',
  'USD₮0',
  'USDXL',
  'feUSD',
  'USDe',
  'USDT0',
]

/** LST (Liquid Staking Token) symbols */
export const LST_SYMBOLS = [
  'stETH',
  'wstETH',
  'rETH',
  'cbETH',
  'sfrxETH',
  'ETHx',
  'osETH',
  'swETH',
  'wstHYPE', // HyperEVM wrapped staked HYPE
  'lstHYPE', // HyperEVM liquid staked HYPE
  'beHYPE', // HyperEVM staking derivative
]

/**
 * Check if a symbol is a stablecoin
 */
export function isStablecoin(symbol: string): boolean {
  return STABLECOIN_SYMBOLS.some((s) => symbol.toUpperCase().includes(s.toUpperCase()))
}

/**
 * Check if a symbol is an LST
 */
export function isLST(symbol: string): boolean {
  return LST_SYMBOLS.some((s) => symbol.toLowerCase().includes(s.toLowerCase()))
}

/**
 * Check if a symbol is a delta neutral asset (e.g., Pendle PT tokens)
 * Delta neutral strategies typically involve principal tokens or wrapped yield-bearing assets
 */
export function isDeltaNeutral(symbol: string): boolean {
  const upper = symbol.toUpperCase()
  return upper.startsWith('PT-')
}
